import React from 'react';
import { ReactComponent as ErrorIcon } from '../../../../assets/images/icons/error.svg';
import { ReactComponent as CheckIcon } from '../../../../assets/images/icons/mevo/ic_check.svg';
import { ReactComponent as WarnIcon } from '../../../../assets/images/icons/warn.svg';
import { Box } from '../../../../components/UI-CHAKRA/Box';
import { Button } from '../../../../components/UI-CHAKRA/Button';
import { Divider } from '../../../../components/UI-CHAKRA/Divider';
import { Flex } from '../../../../components/UI-CHAKRA/Flex';
import { Spinner } from '../../../../components/UI-CHAKRA/Spinner';
import { Text } from '../../../../components/UI-CHAKRA/Text';
import { SNCR_COLORS, SNCR_TEXTS } from '../constants';
import {
  getFailureHeader,
  getItemAlertMessage,
  getItemAlertVariant,
  getResultKind,
  getTypeConfig,
  sortResultItemsByStatus,
  SncrResultKind,
} from '../helpers';
import {
  SncrFeedback,
  SncrNumberingRequestItem,
  SncrNumberingRequestItemResult,
  SncrNumberingRequestResult,
} from '../types';
import { ItemAlertBanner } from './ItemAlertBanner';
import { PrescriptionTypeTag } from './PrescriptionTypeTag';
import { APP_VIEWPORT_HEIGHT } from '../../../../helpers/embeddedViewport';

interface SncrFeedbackModalProps {
  feedback: Exclude<SncrFeedback, { kind: 'error' }>;
  onDismiss(): void;
}

const STATIC_RESULT_HEADERS: Record<Exclude<SncrResultKind, 'failure'>, { title: string; subtitle: string }> = {
  success: { title: SNCR_TEXTS.successTitle, subtitle: SNCR_TEXTS.successSubtitle },
  partial: { title: SNCR_TEXTS.partialTitle, subtitle: SNCR_TEXTS.partialSubtitle },
};

function getResultHeader(result: SncrNumberingRequestResult): { title: string; subtitle: string } {
  const kind = getResultKind(result);
  return kind === 'failure' ? getFailureHeader(result) : STATIC_RESULT_HEADERS[kind];
}

const ICON_SIZE = { width: '18px', height: '18px', flexShrink: 0 } as const;

const ITEMS_MAX_HEIGHT = '320px';

function StatusIcon({ result }: { result?: SncrNumberingRequestItemResult }) {
  if (!result) return <Spinner boxSize={{ base: '14px', md: '16px' }} color="primary.main" flexShrink={0} />;
  if (result.status === 'fulfilled' && !result.alerts?.length)
    return <Box as={CheckIcon} boxSize={{ base: '16px', md: '18px' }} flexShrink={0} />;

  const variant = getItemAlertVariant(result);
  return variant === 'warning' ? (
    <Box as={WarnIcon} boxSize="18px" flexShrink={0} />
  ) : (
    <Box as={ErrorIcon} boxSize="18px" flexShrink={0} />
  );
}

function ItemResultBanner({ item }: { item: SncrNumberingRequestItemResult }) {
  const hasAlert = Boolean(item.alerts?.length);
  if (item.status !== 'failed' && !hasAlert) return null;

  return (
    <ItemAlertBanner variant={getItemAlertVariant(item)} mt="8px">
      {getItemAlertMessage(item)}
    </ItemAlertBanner>
  );
}

function ItemRow({ item, result }: { item: SncrNumberingRequestItem; result?: SncrNumberingRequestItemResult }) {
  const config = getTypeConfig(item.type);

  return (
    <Box border={`1px solid ${SNCR_COLORS.cardBorder}`} borderRadius="12px" padding="12px 16px">
      <Flex alignItems="center" gap="12px">
        <StatusIcon result={result} />
        <PrescriptionTypeTag config={config} />
        <Text fontSize={{ base: '14px', md: '15px' }} fontWeight={{ base: 500, md: 600 }}>
          {config.name}
        </Text>
        <Box flex="1" />
        <Text fontSize={{ base: '10px', md: '12px' }} color={SNCR_COLORS.textMuted} fontWeight={600}>
          REQ.
        </Text>
        <Text fontSize={{ base: '12px', md: '15px' }} fontWeight={700}>
          {item.quantity}
        </Text>
      </Flex>
      {result && <ItemResultBanner item={result} />}
    </Box>
  );
}

export function SncrFeedbackModal({ feedback, onDismiss }: SncrFeedbackModalProps) {
  const isProcessing = feedback.kind === 'processing';

  const header = isProcessing
    ? { title: SNCR_TEXTS.processingTitle, subtitle: SNCR_TEXTS.processingSubtitle }
    : getResultHeader(feedback.result);

  return (
    <Flex
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height={APP_VIEWPORT_HEIGHT}
      zIndex={1500}
      bg={SNCR_COLORS.overlay}
      alignItems="center"
      justifyContent="center"
      padding={{ base: 0, md: '16px' }}
    >
      <Box
        bg="white"
        borderRadius={{ base: 0, md: '16px' }}
        padding={{ base: '24px 16px', md: '32px' }}
        width="100%"
        maxWidth={{ base: '100%', md: '560px' }}
        height={{ base: '100%', md: 'auto' }}
        maxHeight={{ base: '100%', md: `calc(${APP_VIEWPORT_HEIGHT} - 32px)` }}
        display="flex"
        flexDirection="column"
      >
        <Text fontSize="20px" fontWeight={700} mb="4px" flexShrink={0}>
          {header.title}
        </Text>
        <Text fontSize="15px" color={SNCR_COLORS.textBody} mb="20px" flexShrink={0}>
          {header.subtitle}
        </Text>

        <Divider display={{ base: 'block', md: 'none' }} mb="16px" borderColor={SNCR_COLORS.cardBorder} />

        <Flex
          direction="column"
          gap="8px"
          mb="24px"
          flex={{ base: 1, md: 'initial' }}
          maxHeight={{ base: 'none', md: ITEMS_MAX_HEIGHT }}
          overflowY="auto"
          minHeight={0}
        >
          {isProcessing
            ? feedback.items.map((item) => <ItemRow key={item.type} item={item} />)
            : sortResultItemsByStatus(feedback.result.items).map((item) => (
              <ItemRow key={item.type} item={item} result={item} />
            ))}
        </Flex>

        <Button
          width="100%"
          size="lg"
          flexShrink={0}
          borderRadius="full"
          bg="primary.main"
          color="white"
          isLoading={isProcessing}
          loadingText={SNCR_TEXTS.processing}
          spinnerPlacement="start"
          onClick={onDismiss}
        >
          {SNCR_TEXTS.gotIt}
        </Button>
      </Box>
    </Flex>
  );
}
